// cspell:words Kleinpaket Paket
import { assertNever } from "@redotech/util/type";
import {
  AsendiaServices,
  AustraliaPostServices,
  AustriaPostServices,
  BpostServices,
  CanparServices,
  ChitChatsServices,
  ColisPriveServices,
  CorreosServices,
  DhlPaketServices,
  DhlParcelServices,
  DpdServices,
  EcomExpressServices,
  ETAExpressServices,
  EvriServices,
  FirstMileServices,
  FleetOpticsServices,
  getAllServicesArray,
  GlobalEServices,
  GlsServices,
  GofoServices,
  IcsCourierServices,
  IntelcomServices,
  LandmarkGlobalServices,
  OnTracServices,
  P2PServices,
  PassportGlobalServices,
  PosteItalianeServices,
  PostNLServices,
  RoyalMailV3Services,
  Service,
  StallionExpressServices,
  TiktokShopServices,
  UniUniServices,
  UpsMailInnovationsServices,
  VehoServices,
  WhizDeliveryServices,
} from "./carrier-service-catalog";
import {
  AmazonServices,
  ApcServices,
  CanadaPostServices,
  Carriers,
  DhlEcsServices,
  DhlExpressServices,
  EPostGlobalServices,
  FedexServices,
  PurolatorServices,
  SpeeDeeServices,
  UpsServices,
  UspsServices,
} from "./carrier-service-enums";
import {
  amazonServiceToLabel,
  apcServiceToLabel,
  asendiaServiceToLabel,
  australiaPostServiceToLabel,
  austriaPostServiceToLabel,
  bpostServiceToLabel,
  canadaPostServiceToLabel,
  canparServiceToLabel,
  carrierToLabel,
  chitChatsServiceToLabel,
  colisPriveServiceToLabel,
  correosServiceToLabel,
  dhlEcsServiceToLabel,
  dhlExpressServiceToLabel,
  dhlPaketServiceToLabel,
  dhlParcelServiceToLabel,
  dpdServiceToLabel,
  ecomExpressServiceToLabel,
  epostGlobalServiceToLabel,
  etaExpressServiceToLabel,
  evriServiceToLabel,
  fedexServiceToLabel,
  firstMileServiceToLabel,
  fleetOpticsServiceToLabel,
  globalEServiceToLabel,
  glsServiceToLabel,
  gofoServiceToLabel,
  icsCourierServiceToLabel,
  intelcomServiceToLabel,
  isAmazonService,
  isApcService,
  isAsendiaService,
  isAustraliaPostService,
  isAustriaPostService,
  isBpostService,
  isCanadaPostService,
  isCanparService,
  isChitChatsService,
  isColisPriveService,
  isCorreosService,
  isDhlEcsService,
  isDhlExpressService,
  isDhlPaketService,
  isDhlParcelService,
  isDpdService,
  isEcomExpressService,
  isEPostGlobalService,
  isETAExpressService,
  isEvriService,
  isFedexService,
  isFirstMileService,
  isFleetOpticsService,
  isGlobalEService,
  isGlsService,
  isGofoService,
  isIcsCourierService,
  isIntelcomService,
  isLandmarkGlobalService,
  isOnTracService,
  isP2PService,
  isPassportGlobalService,
  isPosteItalianeService,
  isPostNLService,
  isPurolatorService,
  isRoyalMailV3Service,
  isSpeeDeeService,
  isStallionExpressService,
  isTiktokShopService,
  isUniUniService,
  isUpsMailInnovationsService,
  isUpsService,
  isUspsService,
  isVehoService,
  isWhizDeliveryService,
  landmarkGlobalServiceToLabel,
  onTracServiceToLabel,
  p2pServiceToLabel,
  passportGlobalServiceToLabel,
  posteItalianeServiceToLabel,
  postNLServiceToLabel,
  purolatorServiceToLabel,
  royalMailV3ServiceToLabel,
  speeDeeServiceToLabel,
  stallionExpressServiceToLabel,
  tiktokShopServiceToLabel,
  uniUniServiceToLabel,
  upsMailInnovationsServiceToLabel,
  upsServiceToLabel,
  uspsServiceToLabel,
  vehoServiceToLabel,
  whizDeliveryServiceToLabel,
} from "./carriers-and-services";
import { ShippingProviderType } from "./shipment";

export const getPrettyCarrierName = (
  carrier: string,
  shippingProviderType?: ShippingProviderType,
) => {
  if (shippingProviderType === ShippingProviderType.NZ_POST) {
    return "NZ Post";
  }

  if (
    !Object.values(Carriers)
      .map((c) => c.toString())
      .includes(carrier)
  ) {
    return carrier;
  }

  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  const typedCarrier: Carriers = carrier as Carriers;
  return carrierToLabel[typedCarrier];
};

export const getCarrierNameWithTrademark = (carrier: Carriers): string => {
  const prettyName = carrierToLabel[carrier];

  if (
    (carrier === Carriers.UPS ||
      carrier === Carriers.UPS_SUREPOST ||
      carrier === Carriers.UPS_MAIL_INNOVATIONS) &&
    !prettyName.includes("®")
  ) {
    return `${prettyName}®`;
  }

  return prettyName;
};

export const getCarrierServiceLabel = (
  carrier: Carriers | null,
  service: Service | null,
) => {
  if (carrier && service) {
    switch (carrier) {
      case Carriers.UPS_SUREPOST:
      case Carriers.UPS:
        if (isUpsService(service)) {
          switch (service) {
            case UpsServices.NextDayAir:
              return "UPS Next Day Air®";
            case UpsServices.WorldWideSaver:
              return "UPS Worldwide Saver®";
            case UpsServices.NextDayAirEarlyAM:
              return "UPS Next Day Air® Early";
            case UpsServices.NextDayAirSaver:
              return "UPS Next Day Air Saver®";
            case UpsServices.UPSStandard:
              return "UPS® Standard";
            case UpsServices.SecondDayAir:
              return "UPS 2nd Day Air®";
            case UpsServices.WorldWideExpedited:
              return "UPS Worldwide Expedited®";
            case UpsServices.WorldWideEconomyDDU:
              return "UPS Worldwide Economy DDU";
            case UpsServices.SurePostUnder1Lb:
              return "UPS Ground Saver®";
            case UpsServices.SurePostOver1Lb:
              return "UPS Ground Saver®";
            case UpsServices.Ground:
              return "UPS® Ground";
            case UpsServices.ThreeDaySelect:
              return "UPS 3 Day Select®";
            case UpsServices.SecondDayAirAM:
              return "UPS 2nd Air A.M.®";
            case UpsServices.WorldWideExpressPlus:
              return "UPS Worldwide Express Plus®";
            case UpsServices.WorldWideExpress:
              return "UPS Worldwide Express®";
            case UpsServices.ExpressEarly:
              return "UPS Express Early®";
            case UpsServices.ExpressSaver:
              return "UPS Express Saver®";
            case UpsServices.CanadaExpress:
              return "UPS Express®";
            case UpsServices.CanadaExpedited:
              return "UPS Expedited®";
            default:
              assertNever(service);
          }
        }
        break;
      case Carriers.FEDEX_SMART_POST:
      case Carriers.FEDEX:
        if (isFedexService(service)) {
          return fedexServiceToLabel[service];
        }
        break;
      case Carriers.USPS:
      case Carriers.USPS_RETURNS:
      case Carriers.USPS_SHIP:
        if (isUspsService(service)) {
          return uspsServiceToLabel[service];
        }
        break;
      case Carriers.DHL_EXPRESS:
        if (isDhlExpressService(service)) {
          return dhlExpressServiceToLabel[service];
        }
        break;
      case Carriers.DHL_ECS:
        if (isDhlEcsService(service)) {
          return dhlEcsServiceToLabel[service];
        }
        break;
      case Carriers.APC:
        if (isApcService(service)) {
          return apcServiceToLabel[service];
        }
        break;
      case Carriers.AUSTRALIA_POST:
        if (isAustraliaPostService(service)) {
          return australiaPostServiceToLabel[service];
        }
        break;
      case Carriers.CANADA_POST:
        if (isCanadaPostService(service)) {
          return canadaPostServiceToLabel[service];
        }
        break;
      case Carriers.GLS:
        if (isGlsService(service)) {
          return glsServiceToLabel[service];
        }
        break;
      case Carriers.ASENDIA:
        if (isAsendiaService(service)) {
          return asendiaServiceToLabel[service];
        }
        break;
      case Carriers.VEHO:
        if (isVehoService(service)) {
          return vehoServiceToLabel[service];
        }
        break;
      case Carriers.FIRST_MILE:
        if (isFirstMileService(service)) {
          return firstMileServiceToLabel[service];
        }
        break;
      case Carriers.ROYAL_MAIL_V3:
        if (isRoyalMailV3Service(service)) {
          return royalMailV3ServiceToLabel[service];
        }
        break;
      case Carriers.SPEE_DEE:
        if (isSpeeDeeService(service)) {
          return speeDeeServiceToLabel[service];
        }
        break;
      case Carriers.AMAZON:
        if (isAmazonService(service)) {
          return amazonServiceToLabel[service];
        }
        break;
      case Carriers.PASSPORT_GLOBAL:
        if (isPassportGlobalService(service)) {
          return passportGlobalServiceToLabel[service];
        }
        break;
      case Carriers.LANDMARK_GLOBAL:
        if (isLandmarkGlobalService(service)) {
          return landmarkGlobalServiceToLabel[service];
        }
        break;
      case Carriers.ETA_EXPRESS:
        if (isETAExpressService(service)) {
          return etaExpressServiceToLabel[service];
        }
        break;
      case Carriers.EPOST_GLOBAL:
        if (isEPostGlobalService(service)) {
          return epostGlobalServiceToLabel[service];
        }
        break;
      case Carriers.ONTRAC:
        if (isOnTracService(service)) {
          return onTracServiceToLabel[service];
        }
        break;
      case Carriers.GLOBALE:
        if (isGlobalEService(service)) {
          return globalEServiceToLabel[service];
        }
        break;
      case Carriers.UPS_MAIL_INNOVATIONS:
        if (isUpsMailInnovationsService(service)) {
          return upsMailInnovationsServiceToLabel[service];
        }
        break;
      case Carriers.PUROLATOR:
        if (isPurolatorService(service)) {
          return purolatorServiceToLabel[service];
        }
        break;
      case Carriers.UNIUNI:
        if (isUniUniService(service)) {
          switch (service) {
            case UniUniServices.Standard:
              return "UniUni Standard";
            default:
              assertNever(service);
          }
        }
        break;
      case Carriers.STALLION_EXPRESS:
        if (isStallionExpressService(service)) {
          return stallionExpressServiceToLabel[service];
        }
        break;
      case Carriers.ECOM_EXPRESS:
        if (isEcomExpressService(service)) {
          return ecomExpressServiceToLabel[service];
        }
        break;
      case Carriers.CHITCHATS:
        if (isChitChatsService(service)) {
          return chitChatsServiceToLabel[service];
        }
        break;
      case Carriers.DHL_PAKET:
        if (isDhlPaketService(service)) {
          return dhlPaketServiceToLabel[service];
        }
        break;
      case Carriers.DHL_PARCEL:
        if (isDhlParcelService(service)) {
          return dhlParcelServiceToLabel[service];
        }
        break;
      case Carriers.DPD:
        if (isDpdService(service)) {
          return dpdServiceToLabel[service];
        }
        break;
      case Carriers.COLIS_PRIVE:
        if (isColisPriveService(service)) {
          return colisPriveServiceToLabel[service];
        }
        break;
      case Carriers.BPOST:
        if (isBpostService(service)) {
          return bpostServiceToLabel[service];
        }
        break;
      case Carriers.CORREOS:
        if (isCorreosService(service)) {
          return correosServiceToLabel[service];
        }
        break;
      case Carriers.POSTE_ITALIANE:
        if (isPosteItalianeService(service)) {
          return posteItalianeServiceToLabel[service];
        }
        break;
      case Carriers.AUSTRIA_POST:
        if (isAustriaPostService(service)) {
          return austriaPostServiceToLabel[service];
        }
        break;
      case Carriers.EVRI:
        if (isEvriService(service)) {
          return evriServiceToLabel[service];
        }
        break;
      case Carriers.POST_NL:
        if (isPostNLService(service)) {
          return postNLServiceToLabel[service];
        }
        break;
      case Carriers.INTELCOM:
        if (isIntelcomService(service)) {
          return intelcomServiceToLabel[service];
        }
        break;
      case Carriers.FLEET_OPTICS:
        if (isFleetOpticsService(service)) {
          return fleetOpticsServiceToLabel[service];
        }
        break;
      case Carriers.ICS_COURIER:
        if (isIcsCourierService(service)) {
          return icsCourierServiceToLabel[service];
        }
        break;
      case Carriers.WHIZ_DELIVERY:
        if (isWhizDeliveryService(service)) {
          return whizDeliveryServiceToLabel[service];
        }
        break;
      case Carriers.TIKTOK_SHOP:
        if (isTiktokShopService(service)) {
          return tiktokShopServiceToLabel[service];
        }
        break;
      case Carriers.CANPAR_EXPRESS:
        if (isCanparService(service)) {
          return canparServiceToLabel[service];
        }
        break;
      case Carriers.GOFO:
        if (isGofoService(service)) {
          return gofoServiceToLabel[service];
        }
        break;
      case Carriers.P2P_GLOBAL:
        if (isP2PService(service)) {
          return p2pServiceToLabel[service];
        }
        break;
      default:
        assertNever(carrier);
    }

    return getPrettyCarrierName(carrier) + " " + getPrettyServiceName(service);
  } else if (carrier) {
    return getPrettyCarrierName(carrier);
  } else if (service) {
    return getPrettyServiceName(service);
  }

  return "";
};

/** WeGrow codes are generic across carriers, so name the product, not the code. */
const WEGROW_SERVICE_LABELS: Record<string, string> = {
  wegrow_direct_return: "Returns",
  wegrow_domestic_return: "Returns",
  wegrow_home_premium: "Home Delivery",
  wegrow_home_economy: "Home Delivery",
  wegrow_letterbox_premium: "Small Parcel",
  wegrow_letterbox_economy: "Small Parcel",
  wegrow_letterbox_premium_plus: "Small Parcel Plus",
  wegrow_pudo_premium: "Pickup Point",
  wegrow_pudo_economy: "Pickup Point",
};

export const getPrettyServiceName = (
  service: string,
  carrier?: string,
  shippingProviderType?: ShippingProviderType,
) => {
  if (shippingProviderType === ShippingProviderType.NZ_POST) {
    return carrier + " " + service;
  }

  if (shippingProviderType === ShippingProviderType.WEGROW) {
    return WEGROW_SERVICE_LABELS[service] ?? service;
  }

  const knownServices: string[] = getAllServicesArray().map((a) =>
    a.toString(),
  );

  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  if (!knownServices.includes(service as any)) {
    return service;
  }

  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  const typedService = service as Service;

  switch (typedService) {
    case FedexServices.FEDEX_INTERNATIONAL_PRIORITY_EXPRESS:
      return "International Priority Express";
    case FedexServices.FEDEX_INTERNATIONAL_CONNECT_PLUS:
      return "International Connect Plus";
    case FedexServices.FEDEX_INTERNATIONAL_DEFERRED_FREIGHT:
      return "International Deferred Freight";
    case FedexServices.INTERNATIONAL_FIRST:
      return "International First";
    case FedexServices.FEDEX_GROUND:
      return "Ground";
    case FedexServices.FEDEX_GROUND_HOME_DELIVERY:
      return "Ground Home Delivery";
    case FedexServices.FEDEX_2_DAY:
      return "2 Day";
    case FedexServices.PRIORITY_OVERNIGHT:
      return "Priority Overnight";
    case FedexServices.FEDEX_FIRST_FREIGHT:
      return "First Freight";
    case FedexServices.FEDEX_EXPRESS_SAVER:
      return "Express Saver";
    case FedexServices.FEDEX_3_DAY_FREIGHT:
      return "3 Day Freight";
    case FedexServices.FEDEX_2_DAY_AM:
      return "2 Day AM";
    case FedexServices.FEDEX_2_DAY_FREIGHT:
      return "2 Day Freight";
    case FedexServices.INTERNATIONAL_ECONOMY:
      return "International Economy";
    case FedexServices.FIRST_OVERNIGHT:
      return "First Overnight";
    case FedexServices.STANDARD_OVERNIGHT:
      return "Standard Overnight";
    case FedexServices.SMART_POST:
      return "Ground Economy";
    case FedexServices.FEDEX_1_DAY_FREIGHT:
      return "1 Day Freight";
    case FedexServices.INTERNATIONAL_PRIORITY:
      return "International Priority";
    case FedexServices.FEDEX_INTERNATIONAL_PRIORITY:
      return "International Priority";
    case FedexServices.EUROPE_FIRST_INTERNATIONAL_PRIORITY:
      return "Europe First International Priority";
    case FedexServices.INTERNATIONAL_ECONOMY_FREIGHT:
      return "International Economy Freight";
    case FedexServices.INTERNATIONAL_PRIORITY_FREIGHT:
      return "International Priority Freight";
    case UpsServices.Ground:
    case UpsServices.NextDayAir:
    case UpsServices.ThreeDaySelect:
    case UpsServices.SecondDayAirAM:
    case UpsServices.WorldWideSaver:
    case UpsServices.NextDayAirEarlyAM:
    case UpsServices.WorldWideExpressPlus:
    case UpsServices.WorldWideExpress:
    case UpsServices.NextDayAirSaver:
    case UpsServices.UPSStandard:
    case UpsServices.SecondDayAir:
    case UpsServices.WorldWideExpedited:
    case UpsServices.SurePostOver1Lb:
    case UpsServices.SurePostUnder1Lb:
    case UpsServices.ExpressEarly:
    case UpsServices.ExpressSaver:
    case UpsServices.CanadaExpress:
    case UpsServices.CanadaExpedited:
    case UpsServices.WorldWideEconomyDDU:
      return upsServiceToLabel[typedService];
    case UspsServices.GroundAdvantageReturn:
      return "Ground Advantage Return";
    case UspsServices.ExpressMailInternational:
      return "Express Mail International";
    case UspsServices.FirstClassMailInternational:
      return "First Class Mail International";
    case UspsServices.PriorityMailInternational:
      return "Priority Mail International";
    case UspsServices.FirstClassPackageInternationalService:
      return "First Class Package International Service";
    case UspsServices.GroundAdvantage:
      return "Ground Advantage";
    case UspsServices.PriorityMailReturn:
      return "Priority Mail Return";
    case UspsServices.Priority:
      return "Priority";
    case UspsServices.Express:
      return "Express";
    case UspsServices.MediaMail:
      return "Media Mail";
    case UspsServices.LibraryMail:
      return "Library Mail";
    case UspsServices.PriorityMailExpressReturn:
      return "Priority Mail Express Return";
    case UspsServices.FirstClass:
      return "First Class Mail";
    case UspsServices.FirstClassPkgReturn:
      return "First Class Package Return";
    case UspsServices.GroundReturn:
      return "Ground Return";
    case AmazonServices.Ground:
      return "Ground Shipping";
    case DhlExpressServices.BreakBulkEconomy:
      return "Break Bulk Economy";
    case DhlExpressServices.BreakBulkExpress:
      return "Break Bulk Express";
    case DhlExpressServices.DomesticEconomySelect:
      return "Domestic Economy Select";
    case DhlExpressServices.DomesticExpress:
      return "Domestic Express";
    case DhlExpressServices.DomesticExpress1030:
      return "Domestic Express 10:30";
    case DhlExpressServices.DomesticExpress1200:
      return "Domestic Express 12:00";
    case DhlExpressServices.MedicalExpressNonDoc:
      return "Medical Express Non-Doc";
    case DhlExpressServices.MedicalExpress:
      return "Medical Express";
    case DhlExpressServices.EuroPack:
      return "EuroPack";
    case DhlExpressServices.SprintLine:
      return "Sprint Line";
    case DhlExpressServices.ExpressEasy:
      return "Express Easy";
    case DhlExpressServices.ExpressEasyNonDoc:
      return "Express Easy Non-Doc";
    case DhlExpressServices.FreightWorldwide:
      return "Freight Worldwide";
    case DhlExpressServices.JetLine:
      return "Jet Line";
    case DhlExpressServices.SameDay:
      return "Same Day";
    case DhlExpressServices.Express900:
      return "Express 9:00";
    case DhlExpressServices.Express1030:
      return "Express 10:30";
    case DhlExpressServices.Express1200:
      return "Express 12:00";
    case DhlExpressServices.ExpressWorldwide:
      return "Express Worldwide";
    case DhlExpressServices.ExpressWorldwideB2C:
      return "Express Worldwide B2C";
    case DhlExpressServices.ExpressWorldwideNonDoc:
      return "Express Worldwide Non-Doc";
    case DhlExpressServices.ExpressWorldwideB2CNonDoc:
      return "Express Worldwide B2C Non-Doc";
    case DhlExpressServices.SecureLine:
      return "Secure Line";
    case DhlExpressServices.EconomySelect:
      return "Economy Select";
    case DhlExpressServices.EconomySelectNonDoc:
      return "Economy Select Non-Doc";
    case DhlExpressServices.LogisticsServices:
      return "Logistics Services";
    case DhlExpressServices.Express1030NonDoc:
      return "Express 10:30 Non-Doc";
    case DhlExpressServices.Express1200NonDoc:
      return "Express 12:00 Non-Doc";
    case DhlExpressServices.ExpressEnvelope:
      return "Express Envelope";
    case DhlExpressServices.ExpressWorldwideECX:
      return "Express Worldwide ECX";
    case DhlExpressServices.GlobalmailBusiness:
      return "Globalmail Business";
    case DhlExpressServices.JumboBox:
      return "Jumbo Box";
    case DhlExpressServices.EuropackNonDoc:
      return "EuroPack Non-Doc";
    case DhlExpressServices.Express900NonDoc:
      return "Express 9:00 Non-Doc";
    case VehoServices.NextDay:
      return "Next Day";
    case VehoServices.SameDay:
      return "Same Day";
    case VehoServices.GroundPlus:
      return "Ground Plus";
    case VehoServices.PremiumEconomy:
      return "Premium Economy";
    case VehoServices.GroundPlusThree:
      return "Ground Plus Three";
    case FirstMileServices.XParcelGround:
      return "XParcel Ground";
    case FirstMileServices.XParcelPriority:
      return "XParcel Priority";
    case FirstMileServices.XParcelExpedited:
      return "XParcel Expedited";
    case FirstMileServices.XParcelExpeditedPlus:
      return "XParcel Expedited Plus";
    case DhlEcsServices.DhlParcelInternationalStandard:
    case DhlEcsServices.DhlParcelInternationalDirectPriority:
    case DhlEcsServices.DhlParcelInternationalDirectStandard:
    case DhlEcsServices.DhlParcelInternationalDirectSMB:
    case DhlEcsServices.DhlParcelInternationalStandardSMB:
    case DhlEcsServices.DhlBPMExpedited:
    case DhlEcsServices.DhlBPMGround:
    case DhlEcsServices.DhlSmartMailParcelReturnGround:
    case DhlEcsServices.DhlSmartMailParcelReturnPlus:
    case DhlEcsServices.DhlSmartMailParcelReturnLight:
    case DhlEcsServices.DhlParcelExpedited:
    case DhlEcsServices.DhlParcelExpeditedMax:
    case DhlEcsServices.DhlParcelGround:
    case DhlEcsServices.DhlParcelInternationalDirect:
    case DhlEcsServices.DhlPacketInternational:
      return dhlEcsServiceToLabel[typedService];
    case ApcServices.ParcelConnectBookService:
    case ApcServices.ParcelConnectExpeditedDDP:
    case ApcServices.ParcelConnectExpeditedDDU:
    case ApcServices.ParcelConnectPriorityDDP:
    case ApcServices.ParcelConnectPriorityDDPDelcon:
    case ApcServices.ParcelConnectPriorityDDU:
    case ApcServices.ParcelConnectPriorityDDUDelcon:
    case ApcServices.ParcelConnectPriorityDDUPQW:
    case ApcServices.ParcelConnectStandardDDU:
    case ApcServices.ParcelConnectStandardDDUPQW:
    case ApcServices.ParcelConnectPacketDDU:
    case ApcServices.ApcEPMIDDP:
    case ApcServices.ApcEPMEIDDP:
    case ApcServices.ApcPacketDDP:
    case ApcServices.ApcUSPSInternationalPriorityMailExpress:
    case ApcServices.ApcDDUPlus:
    case ApcServices.ApcPriorityDDUPlus:
    case ApcServices.ApcPriorityDDUPlusDelcon:
    case ApcServices.ApcEPacketDDUPlus:
    case ApcServices.ApcPMIDDUPlus:
    case ApcServices.ApcPriorityPQWPlus:
    case ApcServices.ApcUSA:
    case ApcServices.ApcUSAGround:
    case ApcServices.ApcUSAExpress:
    case ApcServices.ApcUSA2:
    case ApcServices.ApcUSA3:
    case ApcServices.ApcPriorityWorldwide:
    case ApcServices.ApcPriorityWorldwideTracked:
    case ApcServices.ApcPriorityWorldwideTrackedDdp:
    case ApcServices.ApcPriorityWorldwideExpedited:
    case ApcServices.ApcPriorityWorldwideExpeditedDdp:
      return apcServiceToLabel[typedService];
    case AustraliaPostServices.ParcelPost:
    case AustraliaPostServices.ExpressPost:
    case AustraliaPostServices.International:
    case AustraliaPostServices.Commercial:
    case AustraliaPostServices.ParcelPostSignature:
    case AustraliaPostServices.ExpressPostSignature:
    case AustraliaPostServices.InternationalSignature:
    case AustraliaPostServices.CommercialSignature:
    case AustraliaPostServices.EParcel1:
    case AustraliaPostServices.EParcelPostReturns:
    case AustraliaPostServices.ExpressParcelPostReturns:
    case AustraliaPostServices.ExpressPostEParcel:
    case AustraliaPostServices.InternationalEconomyAirmailParcels:
    case AustraliaPostServices.InternationalEconomyWSODREGPost:
    case AustraliaPostServices.InternationalExpressDocSecIDocs:
    case AustraliaPostServices.InternationalExpressMerchandise:
    case AustraliaPostServices.InternationalExpressMerchECIMerch:
    case AustraliaPostServices.InternationalStandardPackTrack:
    case AustraliaPostServices.ParcelPostExtra:
    case AustraliaPostServices.ParcelPostWinePlusSignature:
    case AustraliaPostServices.ParcelPostWineSignature:
    case AustraliaPostServices.ExpressEparcelPostReturns:
    case AustraliaPostServices.EparcelPostReturns:
    case AustraliaPostServices.IntlExpressDocs:
    case AustraliaPostServices.IntlStandardPackTrack:
    case AustraliaPostServices.IntLStandardWithSignature:
    case AustraliaPostServices.IntlExpressMerch:
      return australiaPostServiceToLabel[typedService];
    case CanadaPostServices.RegularParcel:
    case CanadaPostServices.ExpeditedParcel:
    case CanadaPostServices.Xpresspost:
    case CanadaPostServices.Priority:
    case CanadaPostServices.ExpeditedParcelUSA:
    case CanadaPostServices.SmallPacketUSAAir:
    case CanadaPostServices.TrackedPacketUSA:
    case CanadaPostServices.TrackedPacketUSALVM:
    case CanadaPostServices.XpresspostUSA:
    case CanadaPostServices.XpresspostInternational:
    case CanadaPostServices.InternationalParcelAir:
    case CanadaPostServices.InternationalParcelSurface:
    case CanadaPostServices.SmallPacketInternationalAir:
    case CanadaPostServices.SmallPacketInternationalSurface:
    case CanadaPostServices.TrackedPacketInternational:
    case CanadaPostServices.ExpeditedParcelPlus:
      return canadaPostServiceToLabel[typedService];
    case GlsServices.EarlyPriorityOvernight:
      return "Early Priority Overnight";
    case GlsServices.PriorityOvernight:
      return "Priority Overnight";
    case GlsServices.CaliforniaParcelService:
      return "California Parcel Service";
    case GlsServices.SaturdayDeliveryService:
      return "Saturday Delivery Service";
    case GlsServices.EarlySaturdayService:
      return "Early Saturday Service";
    case GlsServices.NoonPriorityService:
      return "Noon Priority Service";
    case AsendiaServices.ADS:
    case AsendiaServices.AirFreightInbound:
    case AsendiaServices.AirFreightOutbound:
    case AsendiaServices.AsendiaDomesticBoundPrinterMatterExpedited:
    case AsendiaServices.AsendiaDomesticBoundPrinterMatterGround:
    case AsendiaServices.AsendiaDomesticFlatsExpedited:
    case AsendiaServices.AsendiaDomesticFlatsGround:
    case AsendiaServices.AsendiaDomesticParcelGroundOver1lb:
    case AsendiaServices.AsendiaDomesticParcelGroundUnder1lb:
    case AsendiaServices.AsendiaDomesticParcelMAXOver1lb:
    case AsendiaServices.AsendiaDomesticParcelMAXUnder1lb:
    case AsendiaServices.AsendiaDomesticParcelOver1lbExpedited:
    case AsendiaServices.AsendiaDomesticParcelUnder1lbExpedited:
    case AsendiaServices.AsendiaDomesticPromoParcelExpedited:
    case AsendiaServices.AsendiaDomesticPromoParcelGround:
    case AsendiaServices.BulkFreight:
    case AsendiaServices.BusinessMailCanadaLettermail:
    case AsendiaServices.BusinessMailCanadaLettermailMachineable:
    case AsendiaServices.BusinessMailEconomy:
    case AsendiaServices.BusinessMailEconomyLPWholesale:
    case AsendiaServices.BusinessMailEconomySPWholesale:
    case AsendiaServices.BusinessMailIPA:
    case AsendiaServices.BusinessMailISAL:
    case AsendiaServices.BusinessMailPriority:
    case AsendiaServices.BusinessMailPriorityLPWholesale:
    case AsendiaServices.BusinessMailPrioritySPWholesale:
    case AsendiaServices.MarketingMailCanadaPersonalizedLCP:
    case AsendiaServices.MarketingMailCanadaPersonalizedMachineable:
    case AsendiaServices.MarketingMailCanadaPersonalizedNDG:
    case AsendiaServices.MarketingMailEconomy:
    case AsendiaServices.MarketingMailIPA:
    case AsendiaServices.MarketingMailISAL:
    case AsendiaServices.MarketingMailPriority:
    case AsendiaServices.PublicationsCanadaLCP:
    case AsendiaServices.PublicationsCanadaNDG:
    case AsendiaServices.PublicationsEconomy:
    case AsendiaServices.PublicationsIPA:
    case AsendiaServices.PublicationsISAL:
    case AsendiaServices.PublicationsPriority:
    case AsendiaServices.ePAQElite:
    case AsendiaServices.ePAQEliteCustom:
    case AsendiaServices.ePAQEliteDAP:
    case AsendiaServices.ePAQEliteDDP:
    case AsendiaServices.ePAQEliteDDPOversized:
    case AsendiaServices.ePAQEliteDPD:
    case AsendiaServices.ePAQEliteDirectAccessCanadaDDP:
    case AsendiaServices.ePAQEliteOversized:
    case AsendiaServices.ePAQPlus:
    case AsendiaServices.ePAQPlusCustom:
    case AsendiaServices.ePAQPlusCustomsPrepaid:
    case AsendiaServices.ePAQPlusDAP:
    case AsendiaServices.ePAQPlusDDP:
    case AsendiaServices.ePAQPlusEconomy:
    case AsendiaServices.ePAQPlusWholesale:
    case AsendiaServices.ePAQPlusePacket:
    case AsendiaServices.ePAQPlusePacketCanadaCustomsPrePaid:
    case AsendiaServices.ePAQPlusePacketCanadaDDP:
    case AsendiaServices.ePAQReturnsDomestic:
    case AsendiaServices.ePAQReturnsInternational:
    case AsendiaServices.ePAQSelect:
    case AsendiaServices.ePAQSelectCustom:
    case AsendiaServices.ePAQSelectCustomsPrepaidByShopper:
    case AsendiaServices.ePAQSelectDAP:
    case AsendiaServices.ePAQSelectDDP:
    case AsendiaServices.ePAQSelectDDPDirectAccess:
    case AsendiaServices.ePAQSelectDirectAccess:
    case AsendiaServices.ePAQSelectDirectAccessCanadaDDP:
    case AsendiaServices.ePAQSelectEconomy:
    case AsendiaServices.ePAQSelectOversized:
    case AsendiaServices.ePAQSelectOversizedDDP:
    case AsendiaServices.ePAQSelectPMEI:
    case AsendiaServices.ePAQSelectPMEICanadaCustomsPrePaid:
    case AsendiaServices.ePAQSelectPMEIPCPostage:
    case AsendiaServices.ePAQSelectPMI:
    case AsendiaServices.ePAQSelectPMICanadaCustomsPrepaid:
    case AsendiaServices.ePAQSelectPMICanadaDDP:
    case AsendiaServices.ePAQSelectPMINonPresort:
    case AsendiaServices.ePAQSelectPMIPCPostage:
    case AsendiaServices.ePAQStandard:
    case AsendiaServices.ePAQStandardCustom:
    case AsendiaServices.ePAQStandardEconomy:
    case AsendiaServices.ePAQStandardIPA:
    case AsendiaServices.ePAQStandardISAL:
    case AsendiaServices.ePaqSelectPMEINonPresort:
    case AsendiaServices.ePAQSelectDDPDG:
    case AsendiaServices.AsendiaDomesticParcelMaxUnder70lb:
      return asendiaServiceToLabel[typedService].replace(/^Asendia /, "");
    case RoyalMailV3Services.RoyalMailV3:
    case RoyalMailV3Services.EuropriorityDDP:
    case RoyalMailV3Services.EuropriorityDDPComp1:
    case RoyalMailV3Services.EuropriorityDDPComp2:
    case RoyalMailV3Services.EuropriorityDDPComp3:
    case RoyalMailV3Services.EuropriorityDTPIOSS:
    case RoyalMailV3Services.EuropriorityDTPIOSSComp1:
    case RoyalMailV3Services.EuropriorityDTPIOSSComp2:
    case RoyalMailV3Services.EuropriorityDTPIOSSComp3:
    case RoyalMailV3Services.Express10:
    case RoyalMailV3Services.Express10Comp1:
    case RoyalMailV3Services.Express10Comp2:
    case RoyalMailV3Services.Express10Comp3:
    case RoyalMailV3Services.Express24:
    case RoyalMailV3Services.Express24Comp1:
    case RoyalMailV3Services.Express24Comp2:
    case RoyalMailV3Services.Express24Comp3:
    case RoyalMailV3Services.Express24Weekend:
    case RoyalMailV3Services.Express24WeekendComp1:
    case RoyalMailV3Services.Express24WeekendComp2:
    case RoyalMailV3Services.Express24WeekendComp3:
    case RoyalMailV3Services.Express48:
    case RoyalMailV3Services.Express48Comp1:
    case RoyalMailV3Services.Express48Comp2:
    case RoyalMailV3Services.Express48Comp3:
    case RoyalMailV3Services.Express48Large:
    case RoyalMailV3Services.Express48LargeComp1:
    case RoyalMailV3Services.Express48LargeComp2:
    case RoyalMailV3Services.Express48LargeComp3:
    case RoyalMailV3Services.ExpressAM:
    case RoyalMailV3Services.ExpressAMComp1:
    case RoyalMailV3Services.ExpressAMComp2:
    case RoyalMailV3Services.ExpressAMComp3:
    case RoyalMailV3Services.GlobalpriorityEurope:
    case RoyalMailV3Services.GlobalpriorityEuropeComp1:
    case RoyalMailV3Services.GlobalpriorityEuropeComp2:
    case RoyalMailV3Services.GlobalpriorityEuropeComp3:
    case RoyalMailV3Services.GlobalpriorityROW:
    case RoyalMailV3Services.GlobalpriorityROWComp1:
    case RoyalMailV3Services.GlobalpriorityROWComp2:
    case RoyalMailV3Services.GlobalpriorityROWComp3:
    case RoyalMailV3Services.InternationalBusinessParcelsTrackedPriority:
    case RoyalMailV3Services.Tracked24HighVolumePIN:
    case RoyalMailV3Services.Tracked24PIN:
    case RoyalMailV3Services.Tracked48HighVolumePIN:
    case RoyalMailV3Services.Tracked48PIN:
    case RoyalMailV3Services.TrackedImport24HighVolumePIN:
    case RoyalMailV3Services.TrackedImport48HighVolumePIN:
    case RoyalMailV3Services.DEImportRoyalMail24PlusParcelsWeekendService:
    case RoyalMailV3Services.DEImportStandard24LargeLetter:
    case RoyalMailV3Services.DEImportStandard24LargeLetterWeekendService:
    case RoyalMailV3Services.DEImportStandard24Parcel:
    case RoyalMailV3Services.DEImportStandard24ParcelWeekendService:
    case RoyalMailV3Services.DEImportStandard48LargeLetter:
    case RoyalMailV3Services.DEImportStandard48LargeLetterWeekendService:
    case RoyalMailV3Services.DEImportTracked24ParcelBoxableHighVolumeWeekendService:
    case RoyalMailV3Services.DEImportTracked24ParcelBoxableWeekendService:
    case RoyalMailV3Services.DEImportTracked24ParcelHighVolumeWeekendService:
    case RoyalMailV3Services.DEImportTracked24SignatureParcelHighVolumeWeekendServiceAGE:
    case RoyalMailV3Services.DEImportTracked48ParcelBoxableHighVolumeWeekendService:
    case RoyalMailV3Services.DEImportTracked48ParcelBoxableWeekendService:
    case RoyalMailV3Services.DEImportTracked48ParcelHighVolumeWeekendService:
    case RoyalMailV3Services.DEImportTracked48SignatureParcelHighVolumeAGE:
    case RoyalMailV3Services.DEImportTracked48SignatureParcelHighVolumeWeekendServiceAGE:
    case RoyalMailV3Services.DEImporttoEUMaxSortLargeLetter:
    case RoyalMailV3Services.DEImporttoEUMaxSortParcel:
    case RoyalMailV3Services.DEImporttoEUSignedParcel:
    case RoyalMailV3Services.DEImporttoEUTrackedHighVolLargeLetter:
    case RoyalMailV3Services.DEImporttoEUTrackedParcel:
    case RoyalMailV3Services.DEImporttoEUTrackedSignedLargeLetter:
    case RoyalMailV3Services.DEImporttoEUTrackedSignedParcel:
    case RoyalMailV3Services.HMForcesMail:
    case RoyalMailV3Services.HMForcesSignedFor:
    case RoyalMailV3Services.HMForcesSpecialDelivery1000:
    case RoyalMailV3Services.HMForcesSpecialDelivery2500:
    case RoyalMailV3Services.HMForcesSpecialDelivery500:
    case RoyalMailV3Services.ImportDETracked24:
    case RoyalMailV3Services.ImportDETracked24HighVolume:
    case RoyalMailV3Services.ImportDETracked24Letterboxable:
    case RoyalMailV3Services.ImportDETracked24LetterboxableHighVolume:
    case RoyalMailV3Services.ImportDETracked24SignatureHighVolumeAGE:
    case RoyalMailV3Services.ImportDETracked24SignatureStandardAGE:
    case RoyalMailV3Services.ImportDETracked48:
    case RoyalMailV3Services.ImportDETracked48HighVolume:
    case RoyalMailV3Services.ImportDETracked48Letterboxable:
    case RoyalMailV3Services.ImportDETracked48LetterboxableHighVolume:
    case RoyalMailV3Services.ImportDETrackedReturns24:
    case RoyalMailV3Services.ImportDETrackedReturns48:
    case RoyalMailV3Services.InternationalBusinessMailLargeLetterCountryPricedPriority:
    case RoyalMailV3Services.InternationalBusinessMailLargeLetterCountryPricedPriorityETOE:
    case RoyalMailV3Services.InternationalBusinessMailLargeLetterCountrySortPriority:
    case RoyalMailV3Services.InternationalBusinessMailLargeLetterMaxSortPriorityService:
    case RoyalMailV3Services.InternationalBusinessMailLargeLetterMaxSortResidueStandard:
    case RoyalMailV3Services.InternationalBusinessMailLargeLetterMaxSortStandardService:
    case RoyalMailV3Services.InternationalBusinessMailLargeLetterSemiTracked:
    case RoyalMailV3Services.InternationalBusinessMailLargeLetterZeroSortPriority:
    case RoyalMailV3Services.InternationalBusinessMailLargeLetterZeroSortPriorityMachine:
    case RoyalMailV3Services.InternationalBusinessMailLargeLetterZoneSortPriority:
    case RoyalMailV3Services.InternationalBusinessMailLargeLetterZoneSortPriorityMachine:
    case RoyalMailV3Services.InternationalBusinessMailLetterMaxSortResidueStandard:
    case RoyalMailV3Services.InternationalBusinessMailLettersMaxSortStandard:
    case RoyalMailV3Services.InternationalBusinessMailLettersZeroSortPriority:
    case RoyalMailV3Services.InternationalBusinessMailLettersZoneSortPriority:
    case RoyalMailV3Services.InternationalBusinessMailSignedCountryPriced:
    case RoyalMailV3Services.InternationalBusinessMailSignedExtraCompensationCountryPriced:
    case RoyalMailV3Services.InternationalBusinessMailSignedHighVolCountryPriced:
    case RoyalMailV3Services.InternationalBusinessMailSignedZoneSort:
    case RoyalMailV3Services.InternationalBusinessMailTrackedCountryPriced:
    case RoyalMailV3Services.InternationalBusinessMailTrackedHighVolCountryPriced:
    case RoyalMailV3Services.InternationalBusinessMailTrackedHighVolExtraCompCountryPriced:
    case RoyalMailV3Services.InternationalBusinessMailTrackedLargeLetterCountryPricedETOE:
    case RoyalMailV3Services.InternationalBusinessMailTrackedSignedCountryPriced:
    case RoyalMailV3Services.InternationalBusinessMailTrackedSignedHighVolCountryPriced:
    case RoyalMailV3Services.InternationalBusinessMailTrackedSignedHighVolExtraCompCountryPriced:
    case RoyalMailV3Services.InternationalBusinessMailTrackedSignedLargeLetterCountryPricedETOE:
    case RoyalMailV3Services.InternationalBusinessMailTrackedSignedZoneSort:
    case RoyalMailV3Services.InternationalBusinessMailTrackedZoneSort:
    case RoyalMailV3Services.InternationalBusinessPCTrackedSignedLargeLetterHVExtraCompCountryPricedETOE:
    case RoyalMailV3Services.InternationalBusinessParcelPriorityCountryPricedBoxable:
    case RoyalMailV3Services.InternationalBusinessParcelPriorityCountryPricedBoxableDDP:
    case RoyalMailV3Services.InternationalBusinessParcelTrackedCountryPriced:
    case RoyalMailV3Services.InternationalBusinessParcelTrackedCountryPricedBoxable:
    case RoyalMailV3Services.InternationalBusinessParcelTrackedCountryPricedBoxableDDP:
    case RoyalMailV3Services.InternationalBusinessParcelTrackedCountryPricedBoxableExtraComp:
    case RoyalMailV3Services.InternationalBusinessParcels:
    case RoyalMailV3Services.InternationalBusinessParcelsPrintDirectPriority:
    case RoyalMailV3Services.InternationalBusinessParcelsPrintDirectStandard:
    case RoyalMailV3Services.InternationalBusinessParcelsSignedCountryPriced:
    case RoyalMailV3Services.InternationalBusinessParcelsSignedExtraCompensationCountryPriced:
    case RoyalMailV3Services.InternationalBusinessParcelsSignedExtraCompensationZoneSort:
    case RoyalMailV3Services.InternationalBusinessParcelsSignedZoneSort:
    case RoyalMailV3Services.InternationalBusinessParcelsTrackedCountryPriced:
    case RoyalMailV3Services.InternationalBusinessParcelsTrackedCountryPricedETOE:
    case RoyalMailV3Services.InternationalBusinessParcelsTrackedDirectIrelandCountry:
    case RoyalMailV3Services.InternationalBusinessParcelsTrackedExtraCompCountryPriced:
    case RoyalMailV3Services.InternationalBusinessParcelsTrackedExtraCompZoneSort:
    case RoyalMailV3Services.InternationalBusinessParcelsTrackedSignedCountryPriced:
    case RoyalMailV3Services.InternationalBusinessParcelsTrackedSignedCountryPricedETOE:
    case RoyalMailV3Services.InternationalBusinessParcelsTrackedSignedDDP:
    case RoyalMailV3Services.InternationalBusinessParcelsTrackedSignedExtraCompensationCountryPriced:
    case RoyalMailV3Services.InternationalBusinessParcelsTrackedSignedExtraCompensationZoneSort:
    case RoyalMailV3Services.InternationalBusinessParcelsTrackedSignedZoneSort:
    case RoyalMailV3Services.InternationalBusinessParcelsTrackedZoneSort:
    case RoyalMailV3Services.InternationalBusinessParcelsZeroSortLowVolPriority:
    case RoyalMailV3Services.InternationalBusinessParcelsZeroSortPriority:
    case RoyalMailV3Services.InternationalBusinessParcelsZeroSortPriorityETOE:
    case RoyalMailV3Services.InternationalBusinessParcelsZoneSortPriorityService:
    case RoyalMailV3Services.InternationalBusinessPersonalCorrespondenceLargeLetterCountrySortPriorityETOE:
    case RoyalMailV3Services.InternationalBusinessPersonalCorrespondenceLetterPriorityUntracked:
    case RoyalMailV3Services.InternationalBusinessPersonalCorrespondenceLetterPriorityUntrackedETOE:
    case RoyalMailV3Services.InternationalBusinessPersonalCorrespondenceLetterTrackedHighVolCountryPricedETOE:
    case RoyalMailV3Services.InternationalBusinessPersonalCorrespondenceLetterTrackedSignedHighVolCountryPricedETOE:
    case RoyalMailV3Services.InternationalBusinessPersonalCorrespondenceMaxSortLetter:
    case RoyalMailV3Services.InternationalBusinessPersonalCorrespondenceMaxSortResidueLargeLetter:
    case RoyalMailV3Services.InternationalBusinessPersonalCorrespondenceMaxSortResidueLetter:
    case RoyalMailV3Services.InternationalBusinessPersonalCorrespondenceSignedLargeLetterExtraCompensationCountryPricedETOE:
    case RoyalMailV3Services.InternationalBusinessPersonalCorrespondenceSignedLetterHighVolCountryPricedETOE:
    case RoyalMailV3Services.InternationalBusinessPersonalCorrespondenceTrackedLargeLetterHighVolExtraCompCountryPricedETOE:
    case RoyalMailV3Services.InternationalBusinessPrintedMatterPacket:
    case RoyalMailV3Services.InternationalBusinessTrackedExpressNPC:
    case RoyalMailV3Services.InternationalEconomyOnAccount:
    case RoyalMailV3Services.InternationalSignedOnAccount:
    case RoyalMailV3Services.InternationalSignedOnAccountExtraComp:
    case RoyalMailV3Services.InternationalStandardOnAccount:
    case RoyalMailV3Services.InternationalTrackedOnAccount:
    case RoyalMailV3Services.InternationalTrackedOnAccountExtraComp:
    case RoyalMailV3Services.InternationalTrackedParcels30kg:
    case RoyalMailV3Services.InternationalTrackedParcels30kgCPrio:
    case RoyalMailV3Services.InternationalTrackedParcels30kgETOEC:
    case RoyalMailV3Services.InternationalTrackedParcels30kgETOEE:
    case RoyalMailV3Services.InternationalTrackedParcels30kgExtraComp:
    case RoyalMailV3Services.InternationalTrackedParcels30kgExtraCompETOEC:
    case RoyalMailV3Services.InternationalTrackedParcels30kgExtraCompETOEE:
    case RoyalMailV3Services.InternationalTrackedParcels30kgXCompCPrio:
    case RoyalMailV3Services.InternationalTrackedSignedOnAccount:
    case RoyalMailV3Services.InternationalTrackedSignedOnAccountExtraComp:
    case RoyalMailV3Services.ParcelpostFlatRateAnnual:
    case RoyalMailV3Services.RM24LargeLetterAnnualFlatRate:
    case RoyalMailV3Services.RM24PresortedLargeLetterAnnualFlatRate:
    case RoyalMailV3Services.RM24PresortedParcelsAnnualFlatRate:
    case RoyalMailV3Services.RM48LargeLetterAnnualFlatRate:
    case RoyalMailV3Services.RM48PresortedLargeLetterAnnualFlatRate:
    case RoyalMailV3Services.RM48PresortedParcelsAnnualFlatRate:
    case RoyalMailV3Services.RoyalMail1stClass:
    case RoyalMailV3Services.RoyalMail1stClassLettersDailyRateservice:
    case RoyalMailV3Services.RoyalMail1stClassSignedFor:
    case RoyalMailV3Services.RoyalMail24LargeLetterDailyRate:
    case RoyalMailV3Services.RoyalMail24LargeLetterFlatRate:
    case RoyalMailV3Services.RoyalMail24LargeLetterFlatRateService:
    case RoyalMailV3Services.RoyalMail24PacketpostFlatRateService:
    case RoyalMailV3Services.RoyalMail24ParcelDailyRateService:
    case RoyalMailV3Services.RoyalMail24ParcelSort8DailyRateService:
    case RoyalMailV3Services.RoyalMail24ParcelSort8FlatRateService:
    case RoyalMailV3Services.RoyalMail24ParcelsDailyRate:
    case RoyalMailV3Services.RoyalMail24ParcelsFlatRate:
    case RoyalMailV3Services.RoyalMail24PresortedLargeLetter:
    case RoyalMailV3Services.RoyalMail24PresortedParcels:
    case RoyalMailV3Services.RoyalMail24SORT8LargeLetterAnnualFlatRate:
    case RoyalMailV3Services.RoyalMail24SORT8ParcelsAnnualFlatRate:
    case RoyalMailV3Services.RoyalMail2ndClass:
    case RoyalMailV3Services.RoyalMail2ndClassLettersDailyRateservice:
    case RoyalMailV3Services.RoyalMail2ndClassSignedFor:
    case RoyalMailV3Services.RoyalMail48LargeLetterDailyRate:
    case RoyalMailV3Services.RoyalMail48LargeLetterFlatRate:
    case RoyalMailV3Services.RoyalMail48LargeLetterFlatRateService:
    case RoyalMailV3Services.RoyalMail48PacketpostFlatRateService:
    case RoyalMailV3Services.RoyalMail48ParcelDailyRateService:
    case RoyalMailV3Services.RoyalMail48ParcelSort8DailyRateService:
    case RoyalMailV3Services.RoyalMail48ParcelSort8FlatRateService:
    case RoyalMailV3Services.RoyalMail48ParcelsDailyRate:
    case RoyalMailV3Services.RoyalMail48ParcelsFlatRate:
    case RoyalMailV3Services.RoyalMail48PresortedLargeLetter:
    case RoyalMailV3Services.RoyalMail48PresortedParcels:
    case RoyalMailV3Services.RoyalMail48SORT8LargeLetterAnnualFlatRate:
    case RoyalMailV3Services.RoyalMail48Sort8ParcelsAnnualFlatRate:
    case RoyalMailV3Services.SpecialDeliveryGuaranteed1000:
    case RoyalMailV3Services.SpecialDeliveryGuaranteed2500:
    case RoyalMailV3Services.SpecialDeliveryGuaranteed750:
    case RoyalMailV3Services.SpecialDeliveryGuaranteedAGE1000:
    case RoyalMailV3Services.SpecialDeliveryGuaranteedAGE2500:
    case RoyalMailV3Services.SpecialDeliveryGuaranteedAGE750:
    case RoyalMailV3Services.SpecialDeliveryGuaranteedBy1PM1000:
    case RoyalMailV3Services.SpecialDeliveryGuaranteedBy1PM2500:
    case RoyalMailV3Services.SpecialDeliveryGuaranteedBy1PM750:
    case RoyalMailV3Services.SpecialDeliveryGuaranteedBy1PMAGE1000:
    case RoyalMailV3Services.SpecialDeliveryGuaranteedBy1PMAGE2500:
    case RoyalMailV3Services.SpecialDeliveryGuaranteedBy1PMAGE750:
    case RoyalMailV3Services.SpecialDeliveryGuaranteedBy1PMID1000:
    case RoyalMailV3Services.SpecialDeliveryGuaranteedBy1PMID2500:
    case RoyalMailV3Services.SpecialDeliveryGuaranteedBy1PMID750:
    case RoyalMailV3Services.SpecialDeliveryGuaranteedBy9AM1000:
    case RoyalMailV3Services.SpecialDeliveryGuaranteedBy9AM2500:
    case RoyalMailV3Services.SpecialDeliveryGuaranteedBy9AM750:
    case RoyalMailV3Services.SpecialDeliveryGuaranteedBy9AMAGE1000:
    case RoyalMailV3Services.SpecialDeliveryGuaranteedBy9AMAGE2500:
    case RoyalMailV3Services.SpecialDeliveryGuaranteedBy9AMAGE750:
    case RoyalMailV3Services.SpecialDeliveryGuaranteedBy9AMID1000:
    case RoyalMailV3Services.SpecialDeliveryGuaranteedBy9AMID2500:
    case RoyalMailV3Services.SpecialDeliveryGuaranteedBy9AMID750:
    case RoyalMailV3Services.SpecialDeliveryGuaranteedID1000:
    case RoyalMailV3Services.SpecialDeliveryGuaranteedID2500:
    case RoyalMailV3Services.SpecialDeliveryGuaranteedID750:
    case RoyalMailV3Services.Tracked24:
    case RoyalMailV3Services.Tracked24HighVolume:
    case RoyalMailV3Services.Tracked24HighVolumeSignatureAGE:
    case RoyalMailV3Services.Tracked24SignatureAGE:
    case RoyalMailV3Services.Tracked48:
    case RoyalMailV3Services.Tracked48HighVolume:
    case RoyalMailV3Services.Tracked48HighVolumeSignatureAGE:
    case RoyalMailV3Services.Tracked48SignatureAGE:
    case RoyalMailV3Services.TrackedLetterBoxable24:
    case RoyalMailV3Services.TrackedLetterBoxable24HighVolume:
    case RoyalMailV3Services.TrackedLetterBoxable48:
    case RoyalMailV3Services.TrackedLetterBoxable48HighVolume:
    case RoyalMailV3Services.TrackedReturns24:
    case RoyalMailV3Services.TrackedReturns48:
      return royalMailV3ServiceToLabel[typedService];
    case SpeeDeeServices.SpeeDeeDelivery:
      return "Delivery";
    case PassportGlobalServices.PriorityDdpDelcon:
    case PassportGlobalServices.PriorityDdp:
    case PassportGlobalServices.epacketDdp:
    case PassportGlobalServices.ExpressDdp:
    case PassportGlobalServices.PriorityDduDelcon:
    case PassportGlobalServices.PriorityDdu:
    case PassportGlobalServices.StandardDduDelcon:
    case PassportGlobalServices.ExpeditedDdpDelcon:
    case PassportGlobalServices.ExpeditedDduDelcon:
    case PassportGlobalServices.ExpressDdpDelcon:
    case PassportGlobalServices.GlobalPlus:
      return passportGlobalServiceToLabel[typedService];
    case LandmarkGlobalServices.Delivery:
      return "Delivery";
    case ETAExpressServices.ETAExpress:
      return "ETA Express";
    case EPostGlobalServices.CourierServiceDDP:
    case EPostGlobalServices.CourierServiceDDU:
    case EPostGlobalServices.DomesticEconomyParcel:
    case EPostGlobalServices.DomesticParcelBPM:
    case EPostGlobalServices.DomesticPriorityParcel:
    case EPostGlobalServices.DomesticPriorityParcelBPM:
    case EPostGlobalServices.EMIService:
    case EPostGlobalServices.EconomyParcelService:
    case EPostGlobalServices.IPAService:
    case EPostGlobalServices.ISALService:
    case EPostGlobalServices.PMIService:
    case EPostGlobalServices.PriorityParcelDDP:
    case EPostGlobalServices.PriorityParcelDDU:
    case EPostGlobalServices.PriorityParcelDeliveryConfirmationDDP:
    case EPostGlobalServices.PriorityParcelDeliveryConfirmationDDU:
    case EPostGlobalServices.ePacketService:
      return epostGlobalServiceToLabel[typedService];
    case OnTracServices.Ground:
      return onTracServiceToLabel[typedService];
    case GlobalEServices.Delivery:
      return globalEServiceToLabel[typedService];
    case UpsMailInnovationsServices.StandardFlats:
    case UpsMailInnovationsServices.ParcelSelectLightweightIrreg:
    case UpsMailInnovationsServices.ParcelSelect:
    case UpsMailInnovationsServices.BPMParcels:
    case UpsMailInnovationsServices.BPMFlats:
    case UpsMailInnovationsServices.MediaMail:
    case UpsMailInnovationsServices.MarketingParcels:
    case UpsMailInnovationsServices.EconomyMailInnovations:
    case UpsMailInnovationsServices.ExpeditedMailInnovations:
    case UpsMailInnovationsServices.First:
    case UpsMailInnovationsServices.Priority:
    case UpsMailInnovationsServices.PriorityMailInnovations:
    case UpsMailInnovationsServices.SingleReturns:
      return upsMailInnovationsServiceToLabel[typedService];
    case PurolatorServices.PurolatorExpress:
    case PurolatorServices.PurolatorExpress12PM:
    case PurolatorServices.PurolatorExpressPack12PM:
    case PurolatorServices.PurolatorExpressBox12PM:
    case PurolatorServices.PurolatorExpressEnvelope12PM:
    case PurolatorServices.PurolatorExpress1030AM:
    case PurolatorServices.PurolatorExpress9AM:
    case PurolatorServices.PurolatorExpressBox:
    case PurolatorServices.PurolatorExpressBox1030AM:
    case PurolatorServices.PurolatorExpressBox9AM:
    case PurolatorServices.PurolatorExpressBoxEvening:
    case PurolatorServices.PurolatorExpressBoxInternational:
    case PurolatorServices.PurolatorExpressBoxUS:
    case PurolatorServices.PurolatorExpressEnvelope:
    case PurolatorServices.PurolatorExpressEnvelope1030AM:
    case PurolatorServices.PurolatorExpressEnvelope9AM:
    case PurolatorServices.PurolatorExpressEnvelopeEvening:
    case PurolatorServices.PurolatorExpressEnvelopeInternational:
    case PurolatorServices.PurolatorExpressEnvelopeUS:
    case PurolatorServices.PurolatorExpressEvening:
    case PurolatorServices.PurolatorExpressInternational:
    case PurolatorServices.PurolatorExpressInternational1030AM:
    case PurolatorServices.PurolatorExpressInternational1200:
    case PurolatorServices.PurolatorExpressInternational9AM:
    case PurolatorServices.PurolatorExpressBoxInternational1030AM:
    case PurolatorServices.PurolatorExpressBoxInternational1200:
    case PurolatorServices.PurolatorExpressBoxInternational9AM:
    case PurolatorServices.PurolatorExpressEnvelopeInternational1030AM:
    case PurolatorServices.PurolatorExpressEnvelopeInternational1200:
    case PurolatorServices.PurolatorExpressEnvelopeInternational9AM:
    case PurolatorServices.PurolatorExpressPackInternational1030AM:
    case PurolatorServices.PurolatorExpressPackInternational1200:
    case PurolatorServices.PurolatorExpressPackInternational9AM:
    case PurolatorServices.PurolatorExpressPack:
    case PurolatorServices.PurolatorExpressPack1030AM:
    case PurolatorServices.PurolatorExpressPack9AM:
    case PurolatorServices.PurolatorExpressPackEvening:
    case PurolatorServices.PurolatorExpressPackInternational:
    case PurolatorServices.PurolatorExpressPackUS:
    case PurolatorServices.PurolatorExpressUS:
    case PurolatorServices.PurolatorExpressUS1030AM:
    case PurolatorServices.PurolatorExpressUS1200:
    case PurolatorServices.PurolatorExpressUS9AM:
    case PurolatorServices.PurolatorExpressBoxUS1030AM:
    case PurolatorServices.PurolatorExpressBoxUS1200:
    case PurolatorServices.PurolatorExpressBoxUS9AM:
    case PurolatorServices.PurolatorExpressEnvelopeUS1030AM:
    case PurolatorServices.PurolatorExpressEnvelopeUS1200:
    case PurolatorServices.PurolatorExpressEnvelopeUS9AM:
    case PurolatorServices.PurolatorExpressPackUS1030AM:
    case PurolatorServices.PurolatorExpressPackUS1200:
    case PurolatorServices.PurolatorExpressPackUS9AM:
    case PurolatorServices.PurolatorGround:
    case PurolatorServices.PurolatorGround1030AM:
    case PurolatorServices.PurolatorGround9AM:
    case PurolatorServices.PurolatorGround12PM:
    case PurolatorServices.PurolatorGroundDistribution:
    case PurolatorServices.PurolatorGroundEvening:
    case PurolatorServices.PurolatorGroundUS:
    case PurolatorServices.PurolatorQuickShip:
    case PurolatorServices.PurolatorQuickShipEnvelope:
    case PurolatorServices.PurolatorQuickShipPack:
    case PurolatorServices.PurolatorQuickShipBox:
      return purolatorServiceToLabel[typedService];
    case UniUniServices.Standard:
      return uniUniServiceToLabel[typedService];
    case StallionExpressServices.StallionEconomyUsa:
    case StallionExpressServices.StallionEconomyCa:
    case StallionExpressServices.UsEconomy:
    case StallionExpressServices.UsRegional:
    case StallionExpressServices.PrecisionGround:
      return stallionExpressServiceToLabel[typedService];
    case EcomExpressServices.SameDay:
    case EcomExpressServices.NextDay:
    case EcomExpressServices.Standard:
      return ecomExpressServiceToLabel[typedService];
    case ChitChatsServices.ChitChatsUsEdge:
    case ChitChatsServices.ChitChatsUsConnect:
    case ChitChatsServices.ChitChatsUsSelect:
    case ChitChatsServices.ChitChatsSelect:
    case ChitChatsServices.ChitChatsSlim:
    case ChitChatsServices.ChitChatsCanadaTracked:
    case ChitChatsServices.ChitChatsInternationalTracked:
      return chitChatsServiceToLabel[typedService];
    case DhlPaketServices.Kleinpaket:
    case DhlPaketServices.Paket:
      return dhlPaketServiceToLabel[typedService];
    case DhlParcelServices.ForYou:
      return dhlParcelServiceToLabel[typedService];
    case DpdServices.Home:
      return dpdServiceToLabel[typedService];
    case ColisPriveServices.Home:
      return colisPriveServiceToLabel[typedService];
    case BpostServices.BpackBussable:
    case BpostServices.Bpack24hPro:
      return bpostServiceToLabel[typedService];
    case CorreosServices.Home:
      return correosServiceToLabel[typedService];
    case PosteItalianeServices.Home:
      return posteItalianeServiceToLabel[typedService];
    case AustriaPostServices.Home:
    case AustriaPostServices.SmallParcel:
      return austriaPostServiceToLabel[typedService];
    case EvriServices.Ireland:
    case EvriServices.Uk48:
      return evriServiceToLabel[typedService];
    case PostNLServices.Letterbox:
    case PostNLServices.BelgiumParcels:
    case PostNLServices.InternationalPacket:
    case PostNLServices.InternationalPacketTracked:
      return postNLServiceToLabel[typedService];
    case IntelcomServices.Standard:
      return intelcomServiceToLabel[typedService];
    case FleetOpticsServices.Express:
    case FleetOpticsServices.Ground:
      return fleetOpticsServiceToLabel[typedService];
    case IcsCourierServices.Express:
    case IcsCourierServices.Ground:
      return icsCourierServiceToLabel[typedService];
    case WhizDeliveryServices.Standard:
    case WhizDeliveryServices.NextDay:
      return whizDeliveryServiceToLabel[typedService];
    case TiktokShopServices.CollectionByTiktok:
      return tiktokShopServiceToLabel[typedService];
    case CanparServices.Ground:
    case CanparServices.SelectLetter:
    case CanparServices.SelectPak:
    case CanparServices.Select:
    case CanparServices.OvernightLetter:
    case CanparServices.OvernightPak:
    case CanparServices.Overnight:
    case CanparServices.USALetter:
    case CanparServices.USAPak:
    case CanparServices.SelectUSA:
      return canparServiceToLabel[typedService];
    case GofoServices.ParcelPickup:
    case GofoServices.CanadaDomesticEconomyEast:
    case GofoServices.CanadaDomesticEconomyWest:
    case GofoServices.UsaToCanadaEconomyNortheast:
    case GofoServices.UsaToCanadaEconomyWest:
    case GofoServices.EconomyCentral:
    case GofoServices.EconomyEast:
    case GofoServices.EconomyNortheast:
    case GofoServices.EconomySouth:
    case GofoServices.EconomyWest:
    case GofoServices.UsaToEuEconomyNortheast:
    case GofoServices.ExpeditedNortheast:
    case GofoServices.ExpeditedWest:
    case GofoServices.RegionalCentral:
    case GofoServices.RegionalCharlotte:
    case GofoServices.RegionalCincinnati:
    case GofoServices.RegionalDenver:
    case GofoServices.RegionalEast:
    case GofoServices.RegionalNortheast:
    case GofoServices.RegionalNewJersey:
    case GofoServices.RegionalOrlando:
    case GofoServices.RegionalSoutheast:
    case GofoServices.RegionalSanFrancisco:
    case GofoServices.RegionalSouth:
    case GofoServices.RegionalWest:
    case GofoServices.StandardWest:
    case GofoServices.StandardSaltLakeCity:
    case GofoServices.StandardCentral:
    case GofoServices.StandardNewJersey:
    case GofoServices.StandardNortheast:
    case GofoServices.StandardEast:
    case GofoServices.StandardSoutheast:
    case GofoServices.StandardSouth:
    case GofoServices.CanadaToUsaEconomyEast:
    case GofoServices.UsaToUkEconomyNortheast:
    case GofoServices.ExpeditedNewJersey:
      return gofoServiceToLabel[typedService];
    case P2PServices.ParcelFlexStd:
    case P2PServices.ParcelFlexAdv:
    case P2PServices.ParcelFlexAdvPl:
    case P2PServices.ExpressGnd:
    case P2PServices.Express1D:
    case P2PServices.Express2D:
    case P2PServices.Express3D:
    case P2PServices.IntlCa:
    case P2PServices.IntlExpress:
    case P2PServices.IntlStd:
    case P2PServices.FdxGroundHome:
    case P2PServices.UsGndAdvantage:
      return p2pServiceToLabel[typedService];
    default:
      assertNever(typedService);
  }
};
